import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Widget,
  Box,
  Typography,
  ProgressList,
  ChartLegend,
  Select,
  IconTextButton,
} from '@scope/scope-one-ui/core';
import Download from 'assets/icons/download.svg';
import { COLORS } from '@scope/scope-one-ui/theme';
import { useGetContribution, useGetExportUrl } from '../../services/climateStressTest';
import { Contribution, PageData, TransitionRisk } from '../../types/climateStressTest';
import { Tooltip } from './PhysicalRisk.styled';

export const SectorContribution: FC<PageData> = ({ entity, countries }) => {
  const { t } = useTranslation();

  const [country, setCountry] = useState(entity.country.name);

  const countryIso = countries.find((item) => item.name === country)?.iso!;

  const { data: orderly } = useGetContribution(countryIso, 'orderly');
  const { data: disorderly } = useGetContribution(countryIso, 'disorderly');
  const { data: hotHouse } = useGetContribution(countryIso, 'hotHouse');
  const exportFile = useGetExportUrl('contribution-to-temperature-change', countryIso);

  const getAbs = (dataset?: TransitionRisk<Contribution>) =>
    dataset?.data.map(({ value }) => Math.abs(value)) || [];

  /* Sets maximum scale to 125% of highest available value (abs) to make data nicely visible */
  const max = Math.max(...getAbs(orderly), ...getAbs(disorderly), ...getAbs(hotHouse)) * 1.25;

  const getCount = (count?: TransitionRisk<Contribution>) =>
    count?.data.map((item) => ({
      label: item.sector,
      count: +item.value.toFixed(1),
    })) || [];

  const data = [
    { label: t('Orderly'), color: COLORS.primary.main, count: getCount(orderly) },
    { label: t('Disorderly'), color: COLORS.warning.main, count: getCount(disorderly) },
    { label: t('Hot house'), color: COLORS.error.main, count: getCount(hotHouse) },
  ];

  return (
    <Box display="flex" flexDirection="column" gridGap={2}>
      <Widget
        title={t(
          entity.isSovereign
            ? 'Sector contribution to temperature change'
            : 'Contribution to temperature change',
        )}
        customHeader={
          <IconTextButton icon={<Download />} onClick={exportFile}>
            Export
          </IconTextButton>
        }
        hasFilters={!entity.isSovereign}
        tooltip={
          <Tooltip display="flex" flexDirection="column">
            <Typography variant="body1" color={COLORS.typography.label}>
              {t(
                'Relative contribution of the sector to the average temperature change on country-level',
              )}
            </Typography>
            <ChartLegend items={data} />
          </Tooltip>
        }
      >
        {!entity.isSovereign && (
          <Box display="flex" p={2} justifyContent="flex-end">
            <Select
              value={country}
              label={t('Country')}
              onChange={(val) => setCountry(val as string)}
              options={countries.map(({ name }) => ({ value: name, label: name }))}
            />
          </Box>
        )}
      </Widget>
      <Box display="flex" gridGap={4}>
        {data.map(({ label, count, color }) => (
          <Box key={label} display="flex" flexDirection="column" flex={1}>
            <Widget title={label}>
              <ProgressList
                data={count.map((item) => ({ ...item, color }))}
                percentage
                total={max}
                min={-max}
              />
            </Widget>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
